import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const STEPS = [
  {
    num: '01',
    icon: 'ri-phone-line',
    title: 'Reach Out',
    desc: 'Call or message us any time Mon – Sat, 9am to 9pm. No paperwork, no signup — just let us know you\'re ready.',
    highlight: 'Open 6 days a week',
  },
  {
    num: '02',
    icon: 'ri-music-2-line',
    title: 'Pick Your Instrument',
    desc: 'Choose piano, vocals, or drums. We\'ll confirm your instrument and coordinate a time that works for you.',
    highlight: '3 instruments available',
  },
  {
    num: '03',
    icon: 'ri-map-pin-2-line',
    title: 'We Come to You',
    desc: 'We bring the session to your location — home, community space, wherever works. No travel needed on your end.',
    highlight: 'Mobile · We come to you',
  },
  {
    num: '04',
    icon: 'ri-star-smile-line',
    title: 'Learn & Play',
    desc: 'A full hour of real, focused instruction tailored to your exact level — beginner or returning student. Just $30.',
    highlight: 'Full 60-min · $30 flat',
  },
];

const INSTRUMENTS = [
  {
    icon: 'ri-music-2-line',
    name: 'Piano',
    tag: 'All Skill Levels',
    desc: 'From your very first chord to advanced technique — your instructor comes to you with everything needed to start building real, lasting skill in a single session.',
    perks: ['Beginner-friendly intro', 'Classical & contemporary styles', 'Music reading intro available', 'Equipment brought to your location'],
    img: 'https://public.readdy.ai/ai/img_res/58abeee2ac530ea7d19f19aefba21c69.jpg',
  },
  {
    icon: 'ri-mic-line',
    name: 'Vocals',
    tag: 'Ages 8+',
    desc: 'Work on pitch, tone, breath control, and stage presence with a dedicated vocal coach — right in your own space, no studio required.',
    perks: ['Pitch & breath control', 'Warm-up techniques', 'Performance coaching', 'Genre-flexible instruction'],
    img: 'https://readdy.ai/api/search-image?query=young%20woman%20singing%20into%20a%20professional%20microphone%20in%20a%20cozy%20recording%20studio%20warm%20amber%20lighting%20music%20school%20setting%20focused%20expression%20high%20quality%20photography%20realistic&width=480&height=320&seq=walkin-vocals-card&orientation=landscape',
  },
  {
    icon: 'ri-equalizer-line',
    name: 'Drums',
    tag: 'All Skill Levels',
    desc: 'Feel the groove, build your rhythm, and get solid technique with a dedicated instructor who brings the kit and guides every hit at your location.',
    perks: ['Basic rhythms to fills', 'Hand & foot coordination', 'Kit brought to your location', 'Stick technique fundamentals'],
    img: 'https://readdy.ai/api/search-image?query=drum%20kit%20in%20a%20professional%20music%20studio%20warm%20amber%20lighting%20close%20up%20of%20cymbals%20and%20snare%20drum%20inviting%20music%20school%20atmosphere%20high%20quality%20photography%20realistic&width=480&height=320&seq=walkin-drums-card&orientation=landscape',
  },
];

export default function WalkInProcess() {
  const { ref: stepsRef, isVisible: stepsVis } = useScrollAnimation({ threshold: 0.1 });
  const { ref: instrRef, isVisible: instrVis } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section className="py-28 bg-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-yellow/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-amber-100/40 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-screen-xl mx-auto px-4 sm:px-8">

        {/* ── Steps Section ── */}
        <div ref={stepsRef} className="mb-28">
          <div className="text-center mb-16">
            <span className={`scroll-hidden ${stepsVis ? 'scroll-visible' : ''} inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-yellow/15 text-brand-yellow-dark font-body text-xs font-bold uppercase tracking-widest border border-brand-yellow/30 mb-5`}>
              <i className="ri-flashlight-line" /> Ridiculously Simple
            </span>
            <h2 className={`scroll-hidden delay-100 ${stepsVis ? 'scroll-visible' : ''} font-heading text-5xl lg:text-6xl font-black text-brand-blue-dark leading-tight`}>
              Here&apos;s How Easy It Is
            </h2>
            <p className={`scroll-hidden delay-200 ${stepsVis ? 'scroll-visible' : ''} font-body text-gray-500 text-base max-w-xl mx-auto mt-4 leading-relaxed`}>
              Four steps. Zero friction. We come to you — you could be playing within the hour.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 relative">
            <div className="hidden md:block absolute top-[52px] left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-brand-yellow/20 via-brand-yellow/50 to-brand-yellow/20 pointer-events-none" />
            {STEPS.map((step, i) => (
              <div
                key={step.num}
                className={`scroll-hidden ${stepsVis ? 'scroll-visible' : ''} flex flex-col items-center text-center px-4 relative`}
                style={{ transitionDelay: `${i * 110}ms` }}
              >
                <div className="relative mb-6 z-10">
                  <div className="w-[104px] h-[104px] flex items-center justify-center rounded-full bg-brand-yellow/10 border-2 border-brand-yellow/30">
                    <div className="w-[72px] h-[72px] flex items-center justify-center rounded-full bg-brand-yellow shadow-lg shadow-brand-yellow/30">
                      <i className={`${step.icon} text-2xl text-brand-blue-dark`} />
                    </div>
                  </div>
                  <span className="absolute -top-1 -right-1 w-7 h-7 flex items-center justify-center rounded-full bg-brand-blue-dark text-white font-heading font-black text-xs">
                    {step.num}
                  </span>
                </div>
                <h3 className="font-heading text-brand-blue-dark text-lg font-black mb-2 leading-tight">{step.title}</h3>
                <p className="font-body text-gray-500 text-sm leading-relaxed mb-4">{step.desc}</p>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFFBEB] border border-brand-yellow/30 text-brand-yellow-dark font-body text-xs font-semibold">
                  <i className="ri-check-line text-xs" />
                  {step.highlight}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Instruments Section ── */}
        <div ref={instrRef}>
          <div className="text-center mb-12">
            <span className={`scroll-hidden ${instrVis ? 'scroll-visible' : ''} inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 text-brand-yellow-dark font-body text-xs font-bold uppercase tracking-widest border border-brand-yellow/30 mb-5`}>
              <i className="ri-music-line" /> Choose Your Path
            </span>
            <h2 className={`scroll-hidden delay-100 ${instrVis ? 'scroll-visible' : ''} font-heading text-5xl lg:text-6xl font-black text-brand-blue-dark leading-tight`}>
              What&apos;s Available
            </h2>
            <p className={`scroll-hidden delay-200 ${instrVis ? 'scroll-visible' : ''} font-body text-gray-500 text-base max-w-2xl mx-auto mt-4 leading-relaxed`}>
              Three disciplines. One flat rate. All taught by experienced, passionate instructors who actually care about your progress.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {INSTRUMENTS.map((inst, i) => (
              <div
                key={inst.name}
                className={`scroll-hidden ${instrVis ? 'scroll-visible' : ''} group rounded-2xl bg-white border border-gray-100 hover:border-brand-yellow overflow-hidden transition-all duration-300`}
                style={{ transitionDelay: `${i * 130}ms` }}
              >
                {/* Photo */}
                <div className="relative w-full h-44 overflow-hidden">
                  <img
                    src={inst.img}
                    alt={`${inst.name} lessons at Creative Institute`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <span className="absolute top-3 right-3 px-3 py-1 rounded-full bg-brand-yellow text-brand-blue-dark font-body text-xs font-bold">
                    {inst.tag}
                  </span>
                  <div className="absolute bottom-3 left-4 flex items-center gap-2.5">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border border-white/30">
                      <i className={`${inst.icon} text-white text-base`} />
                    </div>
                    <span className="font-heading text-white text-xl font-black">{inst.name}</span>
                  </div>
                </div>

                {/* Body */}
                <div className="p-6">
                  <p className="font-body text-gray-500 text-sm leading-relaxed mb-5">{inst.desc}</p>
                  <ul className="flex flex-col gap-2.5">
                    {inst.perks.map((perk) => (
                      <li key={perk} className="flex items-center gap-2.5">
                        <div className="w-5 h-5 flex items-center justify-center rounded-full bg-brand-yellow/20 text-brand-yellow-dark flex-shrink-0">
                          <i className="ri-check-line text-xs" />
                        </div>
                        <span className="font-body text-gray-600 text-xs">{perk}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Footer */}
                <div className="px-6 pb-5">
                  <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                    <span className="font-heading text-brand-blue-dark font-black text-sm">$30 / hour</span>
                    <span className="font-body text-gray-400 text-xs">Pay at the door</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
